import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useCallback } from "react";

// Default stale times for different data types
export const STALE_TIMES = {
  // Rarely changing data - cache for 30 minutes
  static: 30 * 60 * 1000,
  // Slowly changing data - cache for 5 minutes
  slow: 5 * 60 * 1000,
  // Frequently changing data - cache for 1 minute
  fast: 60 * 1000,
  // Real-time data - always refetch
  realtime: 0,
} as const;

// GC times (how long to keep in cache after becoming stale)
export const GC_TIMES = {
  short: 5 * 60 * 1000,    // 5 minutes
  medium: 15 * 60 * 1000,  // 15 minutes
  long: 60 * 60 * 1000,    // 1 hour
} as const;

// Prefetch data before navigation
export function usePrefetch() {
  const queryClient = useQueryClient();

  const prefetch = useCallback(
    <T,>(
      queryKey: unknown[],
      queryFn: () => Promise<T>,
      staleTime = STALE_TIMES.slow
    ) => {
      queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime,
      });
    },
    [queryClient]
  );

  return prefetch;
}

// Invalidate related queries
export function useInvalidateQueries() {
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    (queryKeys: unknown[][]) => {
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    [queryClient]
  );

  return invalidate;
}

// Optimistic update helper
export function useOptimisticUpdate<T>() {
  const queryClient = useQueryClient();

  const update = useCallback(
    (
      queryKey: unknown[],
      updater: (oldData: T | undefined) => T
    ) => {
      const previousData = queryClient.getQueryData<T>(queryKey);
      queryClient.setQueryData(queryKey, updater);
      return previousData;
    },
    [queryClient]
  );

  const rollback = useCallback(
    (queryKey: unknown[], previousData: T | undefined) => {
      queryClient.setQueryData(queryKey, previousData);
    },
    [queryClient]
  );

  return { update, rollback };
}

// Standard query options for common patterns
export const queryDefaults = {
  // For user profile data
  profile: {
    staleTime: STALE_TIMES.slow,
    gcTime: GC_TIMES.medium,
    refetchOnWindowFocus: false,
  },
  // For list data (teams, staff, etc.)
  list: {
    staleTime: STALE_TIMES.fast,
    gcTime: GC_TIMES.short,
    refetchOnWindowFocus: true,
  },
  // For static/config data
  config: {
    staleTime: STALE_TIMES.static,
    gcTime: GC_TIMES.long,
    refetchOnWindowFocus: false,
  },
  // For real-time data
  realtime: {
    staleTime: STALE_TIMES.realtime,
    gcTime: GC_TIMES.short,
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // 30 seconds
  },
} as const;
